import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge, Spinner } from 'react-bootstrap';
import { useMobileFeatures } from '../hooks/useMobileFeatures';
import { Capacitor } from '@capacitor/core';

const CapacitorTestSuite = () => {
  const [testResults, setTestResults] = useState({});
  const [isRunning, setIsRunning] = useState(false);
  const { 
    isNative, 
    deviceInfo, 
    networkStatus, 
    getCurrentLocation, 
    takePhoto, 
    triggerHaptic 
  } = useMobileFeatures();

  const runTest = async (testName, testFunction) => {
    setTestResults(prev => ({ ...prev, [testName]: { status: 'running', message: 'Testing...' } }));
    
    try {
      const result = await testFunction();
      setTestResults(prev => ({ 
        ...prev, 
        [testName]: { status: 'success', message: result || 'Test passed' } 
      }));
    } catch (error) {
      setTestResults(prev => ({ 
        ...prev, 
        [testName]: { status: 'error', message: error.message } 
      }));
    }
  };

  const runAllTests = async () => {
    setIsRunning(true);
    setTestResults({});

    // Test 1: Platform Detection
    await runTest('Platform Detection', () => {
      const platform = Capacitor.getPlatform();
      const isNativePlatform = Capacitor.isNativePlatform();
      return `Platform: ${platform}, Native: ${isNativePlatform}`;
    });

    // Test 2: Device Information
    await runTest('Device Information', async () => {
      if (isNative && deviceInfo) {
        return `Device: ${deviceInfo.name}, Platform: ${deviceInfo.platform}, Version: ${deviceInfo.osVersion}`;
      }
      return 'Device info not available (web platform)';
    });

    // Test 3: Network Status
    await runTest('Network Status', async () => {
      if (networkStatus) {
        return `Connected: ${networkStatus.connected}, Type: ${networkStatus.connectionType}`;
      }
      return 'Network status not available';
    });

    // Test 4: Geolocation
    await runTest('Geolocation', async () => {
      const location = await getCurrentLocation();
      if (location) {
        return `Lat: ${location.coords.latitude.toFixed(4)}, Lng: ${location.coords.longitude.toFixed(4)}`;
      }
      return 'Location not available';
    });

    // Test 5: Haptic Feedback
    await runTest('Haptic Feedback', async () => {
      await triggerHaptic();
      return 'Haptic feedback triggered';
    });

    // Test 6: Camera Access
    await runTest('Camera Access', async () => {
      if (isNative) {
        try {
          const photo = await takePhoto();
          return photo ? 'Camera access successful' : 'Camera access failed';
        } catch (error) {
          return `Camera access error: ${error.message}`;
        }
      }
      return 'Camera not available on web platform';
    });

    // Test 7: App State Management
    await runTest('App State Management', () => {
      return 'App state management initialized';
    });

    // Test 8: Offline Detection
    await runTest('Offline Detection', () => {
      const isOnline = navigator.onLine;
      return `Online status: ${isOnline ? 'Online' : 'Offline'}`;
    });

    setIsRunning(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <Badge bg="success">✓ Pass</Badge>;
      case 'error':
        return <Badge bg="danger">✗ Fail</Badge>;
      case 'running':
        return <Badge bg="warning">⟳ Running</Badge>;
      default:
        return <Badge bg="secondary">- Not Run</Badge>;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'danger';
      case 'running':
        return 'warning';
      default:
        return 'light';
    }
  };

  const testCases = [
    { name: 'Platform Detection', key: 'Platform Detection' },
    { name: 'Device Information', key: 'Device Information' },
    { name: 'Network Status', key: 'Network Status' },
    { name: 'Geolocation', key: 'Geolocation' },
    { name: 'Haptic Feedback', key: 'Haptic Feedback' },
    { name: 'Camera Access', key: 'Camera Access' },
    { name: 'App State Management', key: 'App State Management' },
    { name: 'Offline Detection', key: 'Offline Detection' }
  ];

  return (
    <Card className="mb-4">
      <Card.Header>
        <h5 className="mb-0">
          Capacitor Integration Test Suite
          {isNative && <Badge bg="primary" className="ms-2">Native Platform</Badge>}
        </h5>
      </Card.Header>
      <Card.Body>
        <div className="mb-3">
          <Button 
            onClick={runAllTests} 
            disabled={isRunning}
            variant="primary"
            className="me-2"
          >
            {isRunning ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Running Tests...
              </>
            ) : (
              'Run All Tests'
            )}
          </Button>
          <Button 
            onClick={() => setTestResults({})} 
            variant="outline-secondary"
            size="sm"
          >
            Clear Results
          </Button>
        </div>

        <div className="row">
          {testCases.map((test) => {
            const result = testResults[test.key];
            return (
              <div key={test.key} className="col-md-6 mb-3">
                <Alert variant={result ? getStatusColor(result.status) : 'light'}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <strong>{test.name}</strong>
                      {result && (
                        <div className="mt-1">
                          <small className="text-muted">{result.message}</small>
                        </div>
                      )}
                    </div>
                    {getStatusBadge(result?.status)}
                  </div>
                </Alert>
              </div>
            );
          })}
        </div>

        {Object.keys(testResults).length > 0 && (
          <div className="mt-3">
            <h6>Test Summary:</h6>
            <div className="d-flex gap-2">
              <Badge bg="success">
                Passed: {Object.values(testResults).filter(r => r.status === 'success').length}
              </Badge>
              <Badge bg="danger">
                Failed: {Object.values(testResults).filter(r => r.status === 'error').length}
              </Badge>
              <Badge bg="warning">
                Running: {Object.values(testResults).filter(r => r.status === 'running').length}
              </Badge>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default CapacitorTestSuite; 